import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { isDevMode, NgModule, signal } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  tuiButtonOptionsProvider,
  tuiHintOptionsProvider,
  tuiTextfieldOptionsProvider,
} from '@taiga-ui/core';
import { provideEventPlugins } from '@taiga-ui/event-plugins';
import { TuiLanguageName, tuiLanguageSwitcher } from '@taiga-ui/i18n';
import {
  tuiCheckboxOptionsProvider,
  tuiSwitchOptionsProvider,
  tuiTextareaOptionsProvider,
} from '@taiga-ui/kit';
import { provideEchartsCore } from 'ngx-echarts';

import { NightThemeComponent } from '@common/components/night-theme/night-theme.component';
import { OBJECT_CARD_ADAPTER_TOKEN } from '@common/components/object-card-adapter';
import { SidePageModule } from '@common/components/sidepage/sidepage.module';
import { errorInterceptor } from '@common/interceptors/error.interceptor';
import { loaderInterceptor } from '@common/interceptors/loader.interceptor';
import { DeviceModelsEffects } from '@store/deviceModels/device_models.effects';
import { deviceModelsReducer } from '@store/deviceModels/device_models.reducer';
import { CurrentUserEffects } from '@store/effects/current_user.effects';
import { ObjectsEffects } from '@store/effects/objects.effects';
import { currentUserReducer } from '@store/reducers/current_user.reducer';
import { objectsReducer } from '@store/reducers/objects.reducer';

import { apiUrlInterceptor } from './api-url.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CmsCommonComponentsModule } from './cms-common-components.module';
import { ObjectCardAdapterService } from './main/pages/objects/object-card/object-card.adapter';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NightThemeComponent,
    BrowserAnimationsModule,
    CmsCommonComponentsModule,
    SidePageModule,
    StoreModule.forRoot({
      objects: objectsReducer,
      currentUser: currentUserReducer,
      deviceModels: deviceModelsReducer,
    }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    EffectsModule.forRoot([ObjectsEffects, CurrentUserEffects, DeviceModelsEffects]),
  ],
  providers: [
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './common/assets/i18n/', suffix: '.json' }),
    }),
    // Глобальные настройки Taiga UI: только здесь, в корневом инжекторе
    provideEventPlugins(),
    tuiCheckboxOptionsProvider({ size: 's' }),
    tuiSwitchOptionsProvider({ size: 's' }),
    tuiHintOptionsProvider({ appearance: 'custom' }),
    tuiButtonOptionsProvider({ size: 'm' }),
    tuiTextfieldOptionsProvider({ size: signal('m') }),
    tuiTextareaOptionsProvider({ max: 6 }),
    tuiLanguageSwitcher(async (language: TuiLanguageName): Promise<unknown> => {
      switch (language) {
        case `ru`:
        case `russian`:
          return import(`@taiga-ui/i18n/languages/russian`);
        case `fr`:
        case `french`:
          return import(`@taiga-ui/i18n/languages/french`);
        default:
          return import(`@taiga-ui/i18n/languages/english`);
      }
    }),
    { provide: OBJECT_CARD_ADAPTER_TOKEN, useClass: ObjectCardAdapterService },
    provideEchartsCore({ echarts: () => import('echarts') }),
    provideHttpClient(
      withXhr(),
      withInterceptors([apiUrlInterceptor, errorInterceptor, loaderInterceptor]),
    ),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
